/**
 * PlatformHotelServices — Super-Admin editable services configuration for a
 * specific hotel, reachable any time after initial setup. Renders the shared
 * HotelServicesEditor in embedded (light) mode, which auto-saves each toggle
 * and broadcasts a `hocp:services-changed` event so hotel sidebars refresh.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Loader2 } from "lucide-react";
import api from "@/lib/api";
import HotelServicesEditor from "@/pages/hotels/HotelServicesEditor";

export default function PlatformHotelServices() {
  const { hotelId } = useParams();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/platform/v1/hotels/${hotelId}`);
        setHotel(data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [hotelId]);

  return (
    <div className="space-y-8 pb-16" data-testid="platform-hotel-services-page">
      <Link
        to={`/platform/hotels/${hotelId}`}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors"
        data-testid="services-back-link"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Hotel
      </Link>

      <header>
        <div className="text-[12px] uppercase tracking-[0.14em] text-amber-600 font-semibold flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" />
          Services Configuration
        </div>
        <h1 className="text-3xl font-semibold text-slate-900 mt-1">
          {loading ? "Loading…" : <>Edit services for <span className="text-amber-600">{hotel?.name || "this hotel"}</span></>}
        </h1>
        <p className="text-slate-600 mt-2 text-sm max-w-2xl">
          Turn services on or off for this property at any time. Changes save
          instantly and update every hotel user&apos;s sidebar in real time —
          a service that&apos;s off is hidden and its module becomes
          inaccessible. Core modules (Front Desk, Reservations, Housekeeping,
          Maintenance, Finance) are always on.
        </p>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading services…
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8 shadow-sm">
          <HotelServicesEditor hotelId={hotelId} title="" embedded />
        </div>
      )}
    </div>
  );
}
